import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import numbro from 'numbro';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils/style';
import { Text } from '../../typography';

// FIX: this component does not trigger changes when using register
// it's probably something about the value of the ref not changing manually
// so when you watch the field, it won't trigger the changes

const displayNone = { display: 'none' };

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(p) => p.theme.dimensions.inputWidth};
`;

type InputStyledProps = {
  error: boolean;
};
const InputStyled = styled.input<InputStyledProps>`
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  background-clip: padding-box;

  margin: 0;
  margin-bottom: ${(p) => (p.error ? SPACINGS[2] : 0)};
  padding: 0 ${SPACINGS[5]};

  background-color: transparent;
  height: ${SPACINGS[10]};
  width: 100%;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.secondary80w)};
  border-radius: ${(p) => p.theme.dimensions.borderRadius};

  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};
  text-align: right;

  &::placeholder {
    opacity: 0.5;
  }

  &:disabled {
    background-color: ${(p) => p.theme.colors.secondary90w};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:not(:disabled) {
    &:hover {
      border: 1px solid
        ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.black)};
    }
    &:focus {
      outline: none;
      background-color: ${(p) => p.theme.colors.white};
      border: 1px solid
        ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.black)};
      box-shadow: 0 0 0 2px ${(p) => p.theme.colors.white},
        0 0 0 3px ${(p) => p.theme.colors.primary};
    }
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CurrencyStyled = styled.div`
  display: inline-flex;
  position: absolute;
  align-items: center;
  height: ${SPACINGS[10]};
  padding: ${SPACINGS[5]};
`;

type InputNumberProps = {
  name: string;
  error?: string | boolean;
  inputRef?:
    | ((instance: HTMLInputElement | null) => void)
    | React.RefObject<HTMLInputElement>;
  style?: React.CSSProperties;
  defaultValue?: number;
  value?: number;
  onChange?: ((v: number | undefined) => void) | undefined;
  onBlur?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  currency?: string;
  placeholder?: string;
  digits?: number;
};

function InputNumber(props: InputNumberProps) {
  const {
    name,
    inputRef,
    error,
    style,
    defaultValue,
    value: controlledValue,
    onChange,
    onBlur,
    autoFocus,
    disabled,
    currency,
    placeholder,
    digits = 2,
  } = props;

  const theme = useTheme();
  const { t } = useTranslate();
  const errorStyle = React.useMemo(() => ({ color: theme.colors.danger }), [
    theme,
  ]);

  const numbroOptions = React.useMemo(() => ({ mantissa: digits }), [digits]);

  type InputNumberState = {
    value: number | undefined;
    editValue: string;
    displayValue: string;
    isNumberValid: boolean;
    isFocused: boolean;
  };
  const [state, setState] = React.useReducer<
    React.Reducer<InputNumberState, Partial<InputNumberState>>
  >((s, n) => ({ ...s, ...n }), {
    value: controlledValue ?? defaultValue,
    editValue: '',
    displayValue: '',
    isNumberValid: true,
    isFocused: false,
  });

  const { value, editValue, displayValue, isNumberValid, isFocused } = state;

  React.useEffect(() => {
    if (defaultValue) {
      setState({
        displayValue: numbro(defaultValue).format(numbroOptions),
        editValue: numbro(defaultValue).format(numbroOptions),
      });
    }
  }, [defaultValue, numbroOptions]);

  React.useEffect(() => {
    if (controlledValue) {
      const state: Partial<InputNumberState> = {
        value: controlledValue,
        displayValue: numbro(controlledValue).format(numbroOptions),
      };

      if (!isFocused) {
        state.editValue = numbro(controlledValue).format(numbroOptions);
      }

      setState(state);
    }
  }, [controlledValue, numbroOptions, isFocused]);

  React.useEffect(() => {
    if (autoFocus) {
      setState({
        isFocused: true,
        displayValue: value ? numbro(value).format(numbroOptions) : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const targetValue = e.currentTarget.value;

    const state: Partial<InputNumberState> = {
      editValue: e.currentTarget.value,
    };

    if (targetValue) {
      const number = numbro.unformat(e.currentTarget.value);
      const value = number && digits === 0 ? Math.round(number) : number;
      if (typeof value !== 'undefined') {
        const formatted = numbro(value).format(numbroOptions);
        state.displayValue = formatted;
        state.value = value;
        state.isNumberValid = true;
      } else {
        state.isNumberValid = false;
        state.value = undefined;
        state.displayValue = '';
      }
      if (onChange) {
        onChange(value);
      }
    } else {
      state.value = undefined;
      state.displayValue = '';
      state.isNumberValid = true;
      if (onChange) {
        onChange(undefined);
      }
    }

    setState(state);
  }

  function handleFocus() {
    setState({ isFocused: true });
  }

  function handleBlur() {
    if (onBlur) onBlur();

    const state: Partial<InputNumberState> = {
      isFocused: false,
    };

    if (isNumberValid) {
      state.editValue = displayValue;
    }

    setState(state);
  }

  const inputValue = isFocused
    ? editValue
    : !isNumberValid
    ? t('invalidValue')
    : displayValue;

  return (
    <InputWrapper style={style}>
      {currency && (
        <CurrencyStyled>
          <Text margin="none">{currency}</Text>
        </CurrencyStyled>
      )}
      <input
        readOnly
        name={name}
        style={displayNone}
        value={value ?? ''}
        ref={inputRef}
      />
      <InputStyled
        id={name}
        name={name}
        error={Boolean(error)}
        value={inputValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
      />

      {typeof error === 'string' && (
        <Text id={`${name}-error`} style={errorStyle}>
          {error}
        </Text>
      )}
    </InputWrapper>
  );
}

export default React.memo(InputNumber);
